import { useEffect, useRef, useState, type ReactNode } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface ScrollableToolbarRowProps {
  children: ReactNode;
  className?: string;
}

/**
 * A single-row, horizontally scrollable strip for a toolbar.
 *
 * The formatting toolbars in the Djot/AsciiDoc editors pack in
 * heading/format/list/insert actions — more than a narrow editor pane or
 * small viewport can fit on one line. Wrapping the row in plain CSS
 * flex-wrap would make it taller (multiline) instead of scrollable,
 * which misplaces "one row of actions" UI language and crowds the
 * document area. Overflowing to a scrollable row keeps a one-row
 * mental model and lets chevron buttons step through the items.
 *
 * The chevrons only appear when there's actually more content than fits
 * — they're an affordance for overflow, not decoration. `scrollLeft` is
 * used for scroll position so keyboard/arrow-key focus inside the strip
 * doesn't fight with a scroll container's own behavior.
 */
export function ScrollableToolbarRow({ children, className }: ScrollableToolbarRowProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollStart, setCanScrollStart] = useState(false);
  const [canScrollEnd, setCanScrollEnd] = useState(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const update = () => {
      setCanScrollStart(el.scrollLeft > 0);
      setCanScrollEnd(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    };

    update();

    const ro = new ResizeObserver(update);
    ro.observe(el);
    el.addEventListener('scroll', update, { passive: true });
    return () => {
      ro.disconnect();
      el.removeEventListener('scroll', update);
    };
  }, []);

  const scrollBy = (amount: number) => {
    scrollRef.current?.scrollBy({ left: amount, behavior: 'smooth' });
  };

  return (
    <div className={`flex items-center ${className ?? ''}`}>
      {canScrollStart && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-8 shrink-0"
          onClick={() => scrollBy(-240)}
          aria-label="Scroll left"
        >
          <ChevronLeft className="size-4" />
        </Button>
      )}

      <div
        ref={scrollRef}
        className="flex min-w-0 flex-1 items-center overflow-x-auto whitespace-nowrap scrollbar-none"
      >
        {children}
      </div>

      {canScrollEnd && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-8 shrink-0"
          onClick={() => scrollBy(240)}
          aria-label="Scroll right"
        >
          <ChevronRight className="size-4" />
        </Button>
      )}
    </div>
  );
}
